<template>
  <div style="position: relative">
    <label
      v-if="newApView && label"
      class="date-picker__label"
      :class="{ 'date-picker__label--disabled': disabled, 'date-picker__label--floating': labelFloating, 'date-picker__label--with-icon': !hideInputIcon }"
    >{{ label }}</label>
    <!-- @vue-ignore -->
    <VueDatePicker
      v-model="date"
      @update:model-value="updateDate"
      ref="date-picker"
      :ui="uiConfig"
      :locale="pickerLocale"
      :format="displayFormat ?? (enableTimePicker ? '' : 'dd/MM/yyyy')"
      :placeholder="newApView ? undefined : label"
      :disabled="disabled"
      :enable-time-picker="enableTimePicker"
      :time-picker-inline="enableTimePicker"
      :is-24="is24"
      :hide-input-icon="hideInputIcon"
      :month-change-on-scroll="false"
      :teleport="teleportTarget"
      :teleport-center="teleportCenter"
      :clearable="clearable"
      :min-date="minDate"
      :range="range ? { partialRange: false } : false"
      auto-apply
      @open="onOpen"
      @closed="open = false; $emit('closed')"
    ></VueDatePicker>
    <label
      v-if="!newApView && date !== null && date !== undefined && (!range || (range && Array.isArray(date) && date.length > 0))"
      :class="`vue-datepicker__label ${
        disabled ? 'vue-datepicker__label-disabled' : ''
      }`"
      >{{ label }}</label
    >
  </div>
</template>

<style>
.vue-datepicker__label {
  position: absolute;
  top: -10px;
  left: 20px;
  font-size: 12px;
  background-color: white;
  padding: 0 5px;
}
.vue-datepicker__label {
  position: absolute;
  top: -10px;
  left: 20px;
  font-size: 12px;
  background-color: white;
  color: #00000088;
  padding: 0 5px;
}
.date-picker__label {
  position: absolute;
  z-index: 1;
  top: calc(var(--fds-field-height) / 2);
  left: var(--fds-field-label-inset);
  max-width: calc(100% - 2 * var(--fds-field-label-inset));
  overflow: hidden;
  padding: 0 var(--fds-field-label-pad);
  transform: translateY(-50%);
  pointer-events: none;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: var(--fds-text-13);
  line-height: var(--fds-field-label-on-border);
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  transition: top 0.15s ease, left 0.15s ease, font-size 0.15s ease;
}
.date-picker__label--with-icon {
  left: var(--dp-input-icon-padding);
}
.date-picker__label--floating {
  top: 0;
  left: var(--fds-field-label-inset);
  font-size: var(--fds-text-11);
  background: linear-gradient(to bottom, transparent calc(50% - 1px), rgb(var(--v-theme-surface)) calc(50% - 1px));
}
.date-picker__label--disabled {
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
}
.date-picker__label--disabled.date-picker__label--floating {
  background:
    linear-gradient(to bottom, transparent calc(50% - 1px), var(--fds-fill-subtle) calc(50% - 1px)),
    linear-gradient(to bottom, transparent calc(50% - 1px), rgb(var(--v-theme-surface)) calc(50% - 1px));
}
.v-overlay__content > .dp--menu-wrapper {
  margin-top: var(--date-picker-overlay-top, 0px);
  margin-left: var(--date-picker-overlay-left, 0px);
}
.vue-datepicker__label-disabled {
  color: #00000061;
  pointer-events: none;
}

/* Fix z-index for date picker calendar when used in dialogs */
:deep(.dp__menu) {
  z-index: 9999 !important;
  background-color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

/* Ensure the calendar overlay has proper background */
:deep(.dp__overlay) {
  background-color: white !important;
}

/* Fix calendar content background */
:deep(.dp__calendar_wrap) {
  background-color: white !important;
}

/* Fix month/year picker background */
:deep(.dp__month_year_wrap) {
  background-color: white !important;
}

/* Fix action buttons background */
:deep(.dp__action_buttons) {
  background-color: white !important;
}
</style>

<script lang="ts">
import { markRaw } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { useI18n } from "vue-i18n";
import { FEATURES, useFeatureFlag } from "@/features";

export default {
  props: {
    modelValue: {
      type: [Date, Array],
    },
    label: {
      type: String,
      default: "",
    },
    density: {
      type: String,
      default: "default",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    hideInputIcon: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    teleport: {
      type: Boolean,
      default: undefined,
    },
    teleportCenter: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: null,
    },
    roundedClass: {
      type: String,
      default: "",
    },
    range: {
      type: Boolean,
      default: false,
    },
    enableTimePicker: {
      type: Boolean,
      default: false,
    },
    autoOpen: {
      type: Boolean,
      default: false,
    },
    // why: the picker's own time format follows the browser's locale (12h, month first); a screen opts into the app's.
    displayFormat: {
      type: String,
      default: undefined,
    },
    is24: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update", "blur", "closed"],
  setup() {
    const { locale } = useI18n();
    return { locale, newApView: useFeatureFlag(FEATURES.NEW_AP_VIEW) };
  },
  data() {
    return {
      date: this.modelValue,
      format: "dd/MM/yyyy",
      overlayContent: undefined as HTMLElement | null | undefined,
      open: false,
    };
  },
  computed: {
    teleportTarget(): boolean | string | HTMLElement {
      if (this.teleport !== undefined) return this.teleport;
      if (this.overlayContent === undefined) return false;
      return this.overlayContent ?? "body";
    },
    labelFloating(): boolean {
      const empty = this.date === null || this.date === undefined || (Array.isArray(this.date) && this.date.length === 0);
      return this.open || !empty;
    },
    pickerLocale(): string {
      const locales: Record<string, string> = { es: "es-CL", en: "en-US", "pt-BR": "pt-BR" };
      return locales[this.locale] ?? "es-CL";
    },
    uiConfig() {
      return {
        input: `date-input date-input-${this.density} ${
          this.error ? "date-input-error" : ""
        } ${this.roundedClass}`,
      };
    },
  },
  mounted() {
    const overlayContent = (this.$el as HTMLElement).closest?.(".v-overlay__content") as HTMLElement | null;
    this.overlayContent = overlayContent ? markRaw(overlayContent) : null;
    if (this.autoOpen) {
      this.$nextTick(() => (this.$refs["date-picker"] as { openMenu?: () => void } | undefined)?.openMenu?.());
    }
  },
  methods: {
    onOpen() {
      this.open = true;
      if (this.teleport !== undefined || !this.overlayContent) return;
      const { top, left } = this.overlayContent.getBoundingClientRect();
      this.overlayContent.style.setProperty("--date-picker-overlay-top", `${this.overlayContent.scrollTop - top}px`);
      this.overlayContent.style.setProperty("--date-picker-overlay-left", `${this.overlayContent.scrollLeft - left}px`);
    },
    updateDate(newVal: Date | Date[] | undefined) {
      this.$emit("update:modelValue", newVal);
    },
  },
  watch: {
    modelValue(newVal: Date | Date[] | undefined) {
      this.date = newVal;
    },
  },
  components: {
    VueDatePicker,
  },
};
</script>
